import { UserDetailsComponent } from './UserDetails.types';
import { useHistory, useParams } from 'react-router-dom';
import {
  GroupDto,
  GroupsApi,
  UpdateUserStatus,
  UsersApi,
  UserStatus,
  UsersV2Api,
  UserType,
} from '../../api-client';
import { Spinner } from '../../common/components/Spinner/Spinner';
import { Box, Flexbox } from '@hiiretail/synergy-ui';
import { useUserDetailsQueries } from './hooks/useUserDetailsQueries';
import { useAddUserToGroup } from './hooks/useAddUserToGroup';
import { useContext, useEffect, useState } from 'react';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { useQueryClient } from 'react-query';
import { useUpdateUser } from './hooks/useUpdateUser';
import { useResetUserPassword } from './hooks/useResetUserPassword';
import { UserInfo } from './ui/UserInfo/UserInfo';
import { UserGroupsList } from './ui/UserGroupsList/UserGroupsList';
import { EntityNotFound, NewPageWrapper, ServerError, getApiConfiguration } from '../../common';
import { ResetUserPassword } from './ui/ResetUserPassword/ResetUserPassword';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { useAuth } from '@hiiretail/core';
import { AddUserToGroupModal } from './ui/AddUserToGroupModal/AddUserToGroupModal';
import { ActionButton } from '../../common/components/Breadcrumbs/ActionButton/ActionButton';
import { EditUserInfoForm } from './ui/EditUserInfoForm/EditUserInfoForm';
import { useDeleteUserModalFacade } from './hooks/useDeleteUserModalFacade';
import { DeleteConfirmationModal } from '../../common/components/DeleteConfirmationModal/DeleteConfirmationModal';
import { useDeleteUserFromGroupModalFacade } from './hooks/useDeleteUserFromGroupModalFacade';

export const UserDetails: UserDetailsComponent = () => {
  const [isAddUserToGroupModalOpen, setIsAddUserToGroupModalOpen] = useState(false);

  const usersApi = new UsersApi(getApiConfiguration());
  const usersApiV2 = new UsersV2Api(getApiConfiguration());
  const groupsApi = new GroupsApi(getApiConfiguration());
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationsContext);
  const { id } = useParams<{ id: string }>();
  const { userInfo } = useAuth();
  const tenantId = localStorage.getItem('tenantId') as string;
  const history = useHistory();

  const { isLoading, fetchError, userData, effectiveGroups, groups } = useUserDetailsQueries(
    usersApi,
    groupsApi,
    tenantId,
    id as string,
  );

  const deactivateUser = () => {
    if (userData) {
      updateUser({
        updateUserDto: {
          name: userData.name,
          email: userData.email,
          status: UpdateUserStatus.Inactive,
        },
        userId: userData.id,
      });
    }
  };

  const activateUser = () => {
    if (userData) {
      updateUser({
        updateUserDto: {
          name: userData.name,
          email: userData.email,
          status: UpdateUserStatus.Active,
        },
        userId: userData.id,
      });
    }
  };

  const { isDeleteUserModalOpen, toggleUserModalOpen, isDeleting, deleteUser } =
    useDeleteUserModalFacade();

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <>
        {userData?.status === UserStatus.Created && (
          <ActionButton
            title="Deactivate"
            onClick={deactivateUser}
            permission="iam.user.update"
            disabled={isUpdating}
            dataTestId="deactivate-user-btn"
          />
        )}
        {userData?.status === UserStatus.Inactive && (
          <ActionButton
            title="Activate"
            onClick={activateUser}
            permission="iam.user.update"
            disabled={isUpdating}
            dataTestId="activate-user-btn"
          />
        )}
        {id !== userInfo?.id && (
          <ActionButton
            title="Delete user"
            onClick={toggleUserModalOpen}
            permission="iam.user.delete"
            disabled={false}
            dataTestId="delete-user-btn"
          />
        )}
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, userInfo]);

  const {
    mutate: addUserToGroup,
    isLoading: isAdding,
    error: addError,
  } = useAddUserToGroup(usersApi, {
    onSuccess: () => {
      showNotification('User successfully added to the group');
      queryClient.invalidateQueries(`user-${id}-groups`).then();
      queryClient.invalidateQueries('userGroups').then();
      setIsAddUserToGroupModalOpen(false);
    },
    onError: () => {
      showNotification('An error occurred while adding user to group. Please try again');
    },
  });

  const {
    mutate: updateUser,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateUser(usersApiV2, {
    onSuccess: () => {
      showNotification('User information updated successfully');
      queryClient.invalidateQueries([`user-${id}`]).then();
    },
  });

  const {
    mutate: resetPassword,
    isLoading: isResetting,
    error: resetError,
  } = useResetUserPassword(usersApiV2, {
    onSuccess: () => {
      showNotification('Password successfully updated');
      queryClient.invalidateQueries([`user-${id}`]).then();
    },
    onError: () => {
      showNotification('An error occurred while resetting password. Please try again');
    },
  });

  const {
    userGroupToDelete,
    setUserGroupToDelete,
    removeUserFromGroup,
    isRemoving,
    closeUserGroupDeleteModal,
  } = useDeleteUserFromGroupModalFacade();

  const filterGroups = () =>
    groups?.filter(
      (group) => !effectiveGroups?.find((effectiveGroup) => effectiveGroup?.id === group.id),
    ) || [];

  return (
    <NewPageWrapper>
      {isLoading && !fetchError && <Spinner dataTestId="user-details-spinner" />}
      {fetchError && !isLoading && fetchError?.response?.status !== 404 && (
        <ServerError error={fetchError.message} />
      )}
      {fetchError && !isLoading && fetchError?.response?.status === 404 && (
        <EntityNotFound error={fetchError.message} backRoute="/users" itemTitle="User" />
      )}
      {groups?.length && !!userData && (
        <AddUserToGroupModal
          onClose={() => setIsAddUserToGroupModalOpen(false)}
          isOpen={isAddUserToGroupModalOpen}
          isAdding={isAdding}
          error={addError}
          user={userData}
          groups={filterGroups()}
          addUserToGroup={addUserToGroup}
        />
      )}

      <DeleteConfirmationModal
        title={userData?.name || 'Delete user confirmation modal'}
        entity="user"
        isOpen={isDeleteUserModalOpen}
        onClose={toggleUserModalOpen}
        isDeleting={isDeleting}
        deleteEntity={() => deleteUser(id)}
        dataTestId="delete-user-confirmation-modal"
      />
      <DeleteConfirmationModal
        title={
          (userGroupToDelete && userGroupToDelete.name) || 'Delete user group confirmation modal'
        }
        entity="user group"
        isOpen={!!userGroupToDelete}
        onClose={closeUserGroupDeleteModal}
        isDeleting={isRemoving}
        deleteEntity={() =>
          removeUserFromGroup({
            userId: id as string,
            groupId: (userGroupToDelete && userGroupToDelete.documentId) as string,
          })
        }
        dataTestId="delete-user-group-modal"
      />

      {!fetchError && !isLoading && (
        <>
          {!!userData && <UserInfo user={userData} />}
          <Box mt={4} />
          <Flexbox container item gutter={2} style={{ overflow: 'hidden' }} flex="1">
            {!!userData && (
              <EditUserInfoForm
                user={userData}
                isUpdating={isUpdating}
                error={updateError}
                updateUser={updateUser}
              />
            )}
            {groups && effectiveGroups && (
              <UserGroupsList
                isAdding={isAdding}
                isRemoving={isRemoving}
                addUser={() => setIsAddUserToGroupModalOpen(true)}
                groups={effectiveGroups as (GroupDto & { documentId: string })[]}
                setUserGroupToDelete={setUserGroupToDelete}
                onGroupClick={(groupId) => history.push(`/groups/${groupId}`)}
              />
            )}
            {userData?.type === UserType.Standalone && (
              <ResetUserPassword
                isResetting={isResetting}
                resetError={resetError}
                resetUserPassword={resetPassword}
              />
            )}
          </Flexbox>
        </>
      )}
    </NewPageWrapper>
  );
};
